import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ONB } from "@/constants/testIds";
import { Flame, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { kgToLb, lbToKg, cmToIn, inToFtIn, ftInToCm } from "@/lib/units";
import NumField from "@/components/NumField";

const SEX = [
  { v: "male", l: "Male" },
  { v: "female", l: "Female" },
  { v: "other", l: "Other" },
];
const ACTIVITY = [
  { v: "sedentary", l: "Sedentary (desk job, no exercise)" },
  { v: "light", l: "Light (1-3 days/week)" },
  { v: "moderate", l: "Moderate (3-5 days/week)" },
  { v: "active", l: "Active (6-7 days/week)" },
  { v: "very_active", l: "Very Active (2x/day or physical job)" },
];
const GOALS = [
  { v: "fat_loss", l: "Fat Loss" },
  { v: "muscle_gain", l: "Muscle Gain" },
  { v: "strength", l: "Strength" },
  { v: "maintenance", l: "Maintenance" },
  { v: "recomp", l: "Body Recomposition" },
];
const EXP = [
  { v: "beginner", l: "Beginner (< 1 yr)" },
  { v: "intermediate", l: "Intermediate (1-3 yrs)" },
  { v: "advanced", l: "Advanced (3+ yrs)" },
];
const DIET = [
  { v: "omnivore", l: "Omnivore" },
  { v: "vegetarian", l: "Vegetarian" },
  { v: "vegan", l: "Vegan" },
  { v: "pescatarian", l: "Pescatarian" },
];
const EQUIP = ["Bodyweight", "Dumbbells", "Barbell", "Machines", "Cables", "Kettlebells", "Bands", "Pull-up bar"];

const Field = ({ label, children }) => (
  <label className="block">
    <div className="overline mb-1.5">{label}</div>
    {children}
  </label>
);

const inputClass =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze";

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prefillNotice, setPrefillNotice] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    age: 28,
    sex: "male",
    height_cm: 175,
    weight_kg: 75,
    goal_weight_kg: 72,
    activity_level: "moderate",
    fitness_goal: "fat_loss",
    experience: "intermediate",
    workout_days_per_week: 4,
    workout_duration_min: 60,
    equipment: ["Dumbbells", "Barbell"],
    dietary_preference: "omnivore",
    allergies: [],
    food_likes: [],
    food_dislikes: [],
    medical_limitations: [],
  });
  // Raw text for comma-separated fields. The input's source of truth is this
  // string so the user can freely type spaces and commas without them being
  // trimmed/filtered on every keystroke. We re-parse to the array on each
  // change for downstream state and on blur for normalisation.
  const [csvRaw, setCsvRaw] = useState({
    allergies: "",
    food_likes: "",
    food_dislikes: "",
    medical_limitations: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Hydrate the form from a matching /contact lead so users don't type twice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/leads/me/prefill");
        const p = r.data?.prefill;
        if (!p || cancelled) return;
        setForm((f) => ({
          ...f,
          first_name: f.first_name || p.first_name || "",
          age: p.age ?? f.age,
          sex: p.sex || f.sex,
        }));
        const bits = [
          p.first_name && "name",
          p.age && "age",
          p.sex && "sex",
        ].filter(Boolean);
        if (bits.length > 0) {
          setPrefillNotice({
            fields: bits,
            goals: p.goals_text || null,
          });
        }
      } catch {
        // no lead / not authed — silent
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const parseCsv = (str) =>
    str.split(",").map((s) => s.trim()).filter(Boolean);
  const setCsv = (k, str) => {
    setCsvRaw((r) => ({ ...r, [k]: str }));
    set(k, parseCsv(str));
  };
  const blurCsv = (k) => {
    // On blur, normalize the visible text to a clean "a, b, c" form.
    setCsvRaw((r) => ({ ...r, [k]: parseCsv(r[k]).join(", ") }));
  };

  const toggleEquip = (item) => {
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(item)
        ? f.equipment.filter((x) => x !== item)
        : [...f.equipment, item],
    }));
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/profile", form);
      await refresh();
      toast.success("Profile saved — macros calibrated");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "About you",
      content: (
        <div className="space-y-5">
          <div className="flex items-center justify-between border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
            <div>
              <div className="overline">Display units</div>
              <div className="text-xs text-zinc-400 mt-0.5">Stored in metric · shown in your preference</div>
            </div>
            <div className="inline-flex border border-obsidian-600 rounded-sm overflow-hidden" data-testid="onb-units">
              {[
                { v: "metric", l: "cm / kg" },
                { v: "imperial", l: "ft·in / lb" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => set("preferred_units", opt.v)}
                  data-testid={`onb-units-${opt.v}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest ${
                    form.preferred_units === opt.v ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="First name">
              <input data-testid={ONB.firstName} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputClass} />
            </Field>
            <Field label="Age">
              <input data-testid={ONB.age} type="number" min={14} max={90} value={form.age} onChange={(e) => set("age", Number(e.target.value))} className={inputClass} />
            </Field>
            <Field label="Sex">
              <select data-testid={ONB.sex} value={form.sex} onChange={(e) => set("sex", e.target.value)} className={inputClass}>
                {SEX.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
            {form.preferred_units === "imperial" ? (
              <Field label="Height (ft · in)">
                <div className="flex gap-2">
                  <NumField
                    data-testid="onb-height-ft"
                    min={3} max={8}
                    value={inToFtIn(cmToIn(form.height_cm)).ft}
                    onCommit={(raw) => set("height_cm", ftInToCm(Number(raw), inToFtIn(cmToIn(form.height_cm)).in))}
                    className={inputClass + " flex-1"}
                    placeholder="ft"
                  />
                  <NumField
                    data-testid="onb-height-in"
                    min={0} max={11} step={0.5}
                    value={inToFtIn(cmToIn(form.height_cm)).in}
                    onCommit={(raw) => set("height_cm", ftInToCm(inToFtIn(cmToIn(form.height_cm)).ft, Number(raw)))}
                    className={inputClass + " flex-1"}
                    placeholder="in"
                  />
                </div>
              </Field>
            ) : (
              <Field label="Height (cm)">
                <NumField data-testid={ONB.height} value={form.height_cm} onCommit={(raw) => set("height_cm", raw === "" ? "" : Number(raw))} className={inputClass} />
              </Field>
            )}
            {form.preferred_units === "imperial" ? (
              <>
                <Field label="Current weight (lb)">
                  <NumField
                    data-testid={ONB.weight}
                    step="0.1"
                    value={Number(kgToLb(form.weight_kg)).toFixed(1)}
                    onCommit={(raw) => set("weight_kg", Number(lbToKg(Number(raw)).toFixed(2)))}
                    className={inputClass}
                  />
                </Field>
                <Field label="Goal weight (lb)">
                  <NumField
                    data-testid={ONB.goalWeight}
                    step="0.1"
                    value={Number(kgToLb(form.goal_weight_kg)).toFixed(1)}
                    onCommit={(raw) => set("goal_weight_kg", Number(lbToKg(Number(raw)).toFixed(2)))}
                    className={inputClass}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field label="Current weight (kg)">
                  <NumField data-testid={ONB.weight} step="0.1" value={form.weight_kg} onCommit={(raw) => set("weight_kg", raw === "" ? "" : Number(raw))} className={inputClass} />
                </Field>
                <Field label="Goal weight (kg)">
                  <NumField data-testid={ONB.goalWeight} step="0.1" value={form.goal_weight_kg} onCommit={(raw) => set("goal_weight_kg", raw === "" ? "" : Number(raw))} className={inputClass} />
                </Field>
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Training",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Activity level">
            <select data-testid={ONB.activity} value={form.activity_level} onChange={(e) => set("activity_level", e.target.value)} className={inputClass}>
              {ACTIVITY.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Fitness goal">
            <select data-testid={ONB.fitnessGoal} value={form.fitness_goal} onChange={(e) => set("fitness_goal", e.target.value)} className={inputClass}>
              {GOALS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Experience">
            <select data-testid={ONB.experience} value={form.experience} onChange={(e) => set("experience", e.target.value)} className={inputClass}>
              {EXP.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Days / week">
            <NumField data-testid={ONB.workoutDays} min={1} max={7} value={form.workout_days_per_week} onCommit={(raw) => set("workout_days_per_week", raw === "" ? "" : Number(raw))} className={inputClass} />
          </Field>
          <Field label="Duration (minutes)">
            <NumField data-testid={ONB.workoutDuration} min={15} max={180} value={form.workout_duration_min} onCommit={(raw) => set("workout_duration_min", raw === "" ? "" : Number(raw))} className={inputClass} />
          </Field>
          <Field label="Equipment available">
            <div data-testid={ONB.equipment} className="flex flex-wrap gap-2">
              {EQUIP.map((e) => (
                <button
                  type="button"
                  key={e}
                  onClick={() => toggleEquip(e)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
                    form.equipment.includes(e)
                      ? "bg-blaze border-blaze text-white"
                      : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-white"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </Field>
        </div>
      ),
    },
    {
      title: "Nutrition & limits",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Dietary preference">
            <select data-testid={ONB.diet} value={form.dietary_preference} onChange={(e) => set("dietary_preference", e.target.value)} className={inputClass}>
              {DIET.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Allergies (comma separated)">
            <input data-testid={ONB.allergies} value={csvRaw.allergies} onChange={(e) => setCsv("allergies", e.target.value)} onBlur={() => blurCsv("allergies")} className={inputClass} placeholder="peanuts, shellfish" />
          </Field>
          <Field label="Food likes">
            <input data-testid={ONB.likes} value={csvRaw.food_likes} onChange={(e) => setCsv("food_likes", e.target.value)} onBlur={() => blurCsv("food_likes")} className={inputClass} placeholder="chicken, oats, berries" />
          </Field>
          <Field label="Food dislikes">
            <input data-testid={ONB.dislikes} value={csvRaw.food_dislikes} onChange={(e) => setCsv("food_dislikes", e.target.value)} onBlur={() => blurCsv("food_dislikes")} className={inputClass} placeholder="tofu, mushrooms" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Medical limitations / injuries">
              <input data-testid={ONB.medical} value={csvRaw.medical_limitations} onChange={(e) => setCsv("medical_limitations", e.target.value)} onBlur={() => blurCsv("medical_limitations")} className={inputClass} placeholder="lower back, left shoulder" />
            </Field>
          </div>

          {/* Display units — re-exposed on the final step so the user can
              change their mind before submitting onboarding. */}
          <div className="md:col-span-2 border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40 flex items-center justify-between">
            <div>
              <div className="overline">Display units</div>
              <div className="text-xs text-zinc-400 mt-0.5">You can change this any time in Settings</div>
            </div>
            <div className="inline-flex border border-obsidian-600 rounded-sm overflow-hidden" data-testid="onb-units-final">
              {[
                { v: "metric", l: "cm / kg" },
                { v: "imperial", l: "ft·in / lb" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => set("preferred_units", opt.v)}
                  data-testid={`onb-units-final-${opt.v}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest ${
                    form.preferred_units === opt.v ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const last = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-obsidian-900">
      <header className="border-b border-obsidian-600 px-6 py-4 flex items-center gap-2">
        <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
        <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
      </header>

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
        <div className="overline text-blaze mb-2">/ Onboarding · step {step + 1} of {steps.length}</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase mb-8">{steps[step].title}.</h1>

        {step === 0 && prefillNotice && (
          <div
            data-testid="onboarding-prefill-notice"
            className="obs-card !p-4 border-blaze/40 bg-blaze/5 mb-6"
          >
            <div className="overline text-blaze mb-1">/ Pre-filled from your inquiry</div>
            <div className="text-sm text-zinc-300">
              We used your Contact form info to pre-fill your{" "}
              <span className="text-zinc-100">{prefillNotice.fields.join(", ")}</span>. Double-check and edit anything below.
            </div>
            {prefillNotice.goals && (
              <div className="mt-2 text-xs text-zinc-400 italic border-l-2 border-blaze/40 pl-3">
                "{prefillNotice.goals}"
              </div>
            )}
          </div>
        )}

        <div className="obs-card animate-fade-up">
          {steps[step].content}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep(step - 1)}
            data-testid={ONB.back}
            className="ghost-btn px-5 py-2.5 text-xs disabled:opacity-30 flex items-center gap-2"
          >
            <ChevronLeft className="h-3 w-3" /> Back
          </button>
          {last ? (
            <button data-testid={ONB.submit} onClick={submit} disabled={loading} className="blaze-btn px-6 py-2.5 text-xs disabled:opacity-50 flex items-center gap-2">
              {loading ? "Saving..." : "Finish & calibrate"} <ChevronRight className="h-3 w-3" />
            </button>
          ) : (
            <button data-testid={ONB.next} onClick={() => setStep(step + 1)} className="blaze-btn px-6 py-2.5 text-xs flex items-center gap-2">
              Next <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
